import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, FileText, FolderOpen, Globe, Radio, Search, SlidersHorizontal, X } from '@digital-twins/design-system/icons'
import i18n from '@/i18n'
import { ENTITY_ICONS } from '@/lib/icons/entityIcons'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import {
  knowledgeApi,
  KnowledgeCollectionItem,
} from '@/lib/api'
import {
  KnowledgeAskSourceScope,
  KnowledgeDocumentListItem,
  RoomSources,
  RoomSourcesMode,
} from '@digital-twins/shared-types'
import { isScopeChannelOn } from './threadHelpers'

export interface KnowledgeScopeSelection extends KnowledgeAskSourceScope {
  sourceMode?: 'auto' | 'manual'
  web?: boolean
  library?: boolean
  mode: 'all' | 'selected'
  documentTokens: Array<string>
  collectionTokens: Array<string>
}

interface PickerOpenProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ThreadScopeProps extends PickerOpenProps {
  value: KnowledgeScopeSelection
  onApply: (value: KnowledgeScopeSelection) => void
  documents?: Array<KnowledgeDocumentListItem> | null
  collections?: Array<KnowledgeCollectionItem> | null
  roomSources?: undefined
}

interface RoomScopeProps extends PickerOpenProps {
  /** The room composer's sources (D12): Auto or Custom with Library, Social listening, Web search. */
  roomSources: RoomSourcesProps
}

/**
 * Room sources (D12, D13): the same picker in the room composer. Each key present names a
 * source the room cannot use, with the short reason the row shows instead of its description.
 */
export interface RoomSourcesUnavailable {
  social?: string
  web?: string
}

interface RoomSourcesProps {
  value: RoomSources
  onApply: (value: RoomSources) => void
  unavailable: RoomSourcesUnavailable
}

type KnowledgeScopePickerProps = ThreadScopeProps | RoomScopeProps

interface ChannelRowProps {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  icon: typeof Globe
  title: string
  description: string
  testId: string
  /** Shown instead of `description`; the row is off and cannot be switched. */
  disabledReason?: string
  /** Shows the row's state without letting it be switched. */
  locked?: boolean
  /** A short tag beside the title (room Auto: Recommended). */
  badge?: string
  /** The room picker's tighter rows. */
  compact?: boolean
}

type PickerTab = 'documents' | 'collections'

function fileTypeLabel(filename: string | null | undefined): string {
  const fallback = i18n.t('threads.scope.fileTypeFallback', { ns: 'app' })
  if (!filename) return fallback
  const ext = filename.split('.').pop()?.toUpperCase()
  return ext || fallback
}

function matchesQuery(haystack: string, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return haystack.toLowerCase().includes(needle)
}

function ChannelRow(props: ChannelRowProps) {
  const { onCheckedChange, icon: Icon, title, description, testId, disabledReason, locked, badge } = props
  const isDisabled = disabledReason !== undefined
  const checked = props.checked && !isDisabled
  const cannotSwitch = isDisabled || locked === true
  const descriptionId = `${testId}-description`

  return (
    <div
      className="flex min-h-11 items-center gap-3 border-b border-border/50 py-2.5 last:border-b-0"
      title={isDisabled ? disabledReason : description}
    >
      <button
        type="button"
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isDisabled ? 'disabled:cursor-not-allowed' : 'disabled:cursor-default',
        )}
        data-testid={`${testId}-label`}
        disabled={cannotSwitch}
        aria-describedby={descriptionId}
        onClick={() => onCheckedChange(!checked)}
      >
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <Text as="span" size="sm" className="shrink-0 font-normal leading-5">{title}</Text>
        {badge ? <span className="sr-only" data-testid={`${testId}-badge`}>{badge}</span> : null}
        {isDisabled ? (
          <Text as="span" size="xs" color="muted" className="truncate" data-testid={`${testId}-reason`}>
            {disabledReason}
          </Text>
        ) : null}
      </button>
      <span id={descriptionId} className="sr-only">{isDisabled ? disabledReason : description}</span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={cannotSwitch}
        aria-label={title}
        aria-describedby={descriptionId}
        data-testid={testId}
      />
    </div>
  )
}

/**
 * One source picker for threads and rooms (D13). Threads pick web, library and library items;
 * a room picks Auto or Custom over Library, Social listening and Web search.
 */
export function KnowledgeScopePicker(props: KnowledgeScopePickerProps) {
  if (props.roomSources) {
    return (
      <RoomSourcesScope
        open={props.open}
        onOpenChange={props.onOpenChange}
        {...props.roomSources}
      />
    )
  }
  return <ThreadScope {...props} />
}

function ThreadScope(props: ThreadScopeProps) {
  const {
    open,
    onOpenChange,
    value,
    onApply,
    documents: documentsProp = null,
    collections: collectionsProp = null,
  } = props
  const { t } = useTranslation('app')

  const [tab, setTab] = useState<PickerTab>('documents')
  const [documents, setDocuments] = useState<Array<KnowledgeDocumentListItem>>(
    documentsProp || [],
  )
  const [collections, setCollections] = useState<Array<KnowledgeCollectionItem>>(
    collectionsProp || [],
  )
  const [loading, setLoading] = useState(false)
  const [sourceMode, setSourceMode] = useState<'auto' | 'manual'>(value.sourceMode ?? 'auto')
  const [social, setSocial] = useState(() => !value.sourceKinds || value.sourceKinds.includes('social'))
  const [web, setWeb] = useState(() => isScopeChannelOn(value.web))
  const [library, setLibrary] = useState(() => isScopeChannelOn(value.library) && (!value.sourceKinds || value.sourceKinds.includes('docs')))
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(
    () => new Set(value.documentTokens),
  )
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(
    () => new Set(value.collectionTokens),
  )
  const [filterOpen, setFilterOpen] = useState(
    () => value.documentTokens.length > 0 || value.collectionTokens.length > 0,
  )
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) return
    setSourceMode(value.sourceMode ?? 'auto')
    setSocial(!value.sourceKinds || value.sourceKinds.includes('social'))
    setWeb(isScopeChannelOn(value.web))
    setLibrary(isScopeChannelOn(value.library) && (!value.sourceKinds || value.sourceKinds.includes('docs')))
    setSelectedDocs(new Set(value.documentTokens))
    setSelectedCollections(new Set(value.collectionTokens))
    setFilterOpen(
      value.documentTokens.length > 0 || value.collectionTokens.length > 0,
    )
    setQuery('')
  }, [open, value.sourceMode, value.sourceKinds, value.web, value.library, value.documentTokens, value.collectionTokens])

  useEffect(() => {
    if (!open) return
    if (documentsProp && collectionsProp) {
      setDocuments(documentsProp)
      setCollections(collectionsProp)
      return
    }
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const [docs, cols] = await Promise.all([
          documentsProp
            ? Promise.resolve(documentsProp)
            : knowledgeApi.listDocuments(),
          collectionsProp
            ? Promise.resolve(collectionsProp)
            : knowledgeApi.listCollections(),
        ])
        if (!cancelled) {
          setDocuments(docs)
          setCollections(cols)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, documentsProp, collectionsProp])

  const untitledDocument = t('threads.scope.untitledDocument')
  const filteredDocuments = useMemo(
    () =>
      documents.filter((doc) =>
        matchesQuery(doc.originalFilename || untitledDocument, query),
      ),
    [documents, query, untitledDocument],
  )
  const filteredCollections = useMemo(
    () => collections.filter((collection) => matchesQuery(collection.name, query)),
    [collections, query],
  )

  const selectedCount = selectedDocs.size + selectedCollections.size
  const canApply = sourceMode === 'auto' || web || library || social

  const selectedLabel = useMemo(() => {
    if (sourceMode === 'auto' || !library || selectedCount === 0) {
      return t('threads.scope.apply')
    }
    return t('threads.scope.useItems', { count: selectedCount })
  }, [sourceMode, library, selectedCount, t])

  function toggleDoc(token: string) {
    setSelectedDocs((prev) => {
      const next = new Set(prev)
      if (next.has(token)) next.delete(token)
      else next.add(token)
      return next
    })
  }

  function toggleCollection(token: string) {
    setSelectedCollections((prev) => {
      const next = new Set(prev)
      if (next.has(token)) next.delete(token)
      else next.add(token)
      return next
    })
  }

  const isAuto = sourceMode === 'auto'

  function resetAutoSources() {
    setLibrary(true)
    setSocial(true)
    setWeb(true)
    setSelectedDocs(new Set())
    setSelectedCollections(new Set())
    setFilterOpen(false)
  }

  function setAuto(checked: boolean) {
    setSourceMode(checked ? 'auto' : 'manual')
    resetAutoSources()
  }

  // Same interaction as the room picker: an individual switch leaves Auto
  // and preserves every other source as currently displayed.
  function switchSource(set: (checked: boolean) => void) {
    return (checked: boolean) => {
      if (isAuto) {
        setSourceMode('manual')
        resetAutoSources()
      }
      set(checked)
    }
  }

  function handleApply() {
    if (!canApply) {
      return
    }
    const documentTokens = Array.from(selectedDocs)
    const collectionTokens = Array.from(selectedCollections)
    const hasItems = documentTokens.length > 0 || collectionTokens.length > 0
    onApply({
      sourceMode,
      web: isAuto || web,
      library: isAuto || library || social,
      sourceKinds: isAuto || (library && social) ? undefined : library ? ['docs'] : social ? ['social'] : undefined,
      ...(value.socialWindow ? { socialWindow: value.socialWindow } : {}),
      ...(value.socialFocus ? { socialFocus: value.socialFocus } : {}),
      mode: sourceMode === 'manual' && library && hasItems ? 'selected' : 'all',
      documentTokens: sourceMode === 'manual' && library && hasItems ? documentTokens : [],
      collectionTokens: sourceMode === 'manual' && library && hasItems ? collectionTokens : [],
    })
    onOpenChange(false)
  }

  const filterSummary =
    selectedCount === 0
      ? t('threads.scope.allItems')
      : t('threads.scope.selectedCount', { count: selectedCount })

  const libraryFilter = sourceMode === 'manual' && library ? (
    <div className="min-h-0 min-w-0 overflow-y-auto border-t border-border">
      <button
        type="button"
        aria-expanded={filterOpen}
        aria-controls="knowledge-scope-filter-panel"
        data-testid="knowledge-scope-filter-toggle"
        className="flex w-full items-center gap-2 px-5 py-3 text-left transition-colors hover:bg-muted/40"
        onClick={() => setFilterOpen((prev) => !prev)}
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            filterOpen ? 'rotate-0' : '-rotate-90',
          )}
        />
        <Text as="span" size="sm" className="flex-1 leading-5">
          {t('threads.scope.libraryItemsLabel')}
        </Text>
        <Badge variant="tag">{filterSummary}</Badge>
      </button>

      <div
        id="knowledge-scope-filter-panel"
        hidden={!filterOpen}
        data-testid="knowledge-scope-filter-panel"
      >
        <div className="px-5">
          <Tabs
            value={tab}
            onValueChange={(next) => setTab(next as PickerTab)}
          >
            <TabsList
              className="h-8 w-full rounded-lg bg-muted p-0.5"
              data-testid="knowledge-scope-tabs"
            >
              <TabsTrigger
                value="documents"
                variant="segmented"
                className="h-7 flex-1"
                data-testid="knowledge-scope-tab-documents"
              >
                {t('threads.scope.tabDocuments')}
              </TabsTrigger>
              <TabsTrigger
                value="collections"
                variant="segmented"
                className="h-7 flex-1"
                data-testid="knowledge-scope-tab-collections"
              >
                {t('threads.scope.tabCollections')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="px-5 pt-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('threads.scope.searchPlaceholder')}
              aria-label={t('threads.scope.searchAria')}
              className="h-8 rounded-lg bg-muted/40 pl-8 pr-8 text-sm"
              data-testid="knowledge-scope-search"
            />
            {query ? (
              <button
                type="button"
                aria-label={t('threads.scope.searchClear')}
                className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
                data-testid="knowledge-scope-search-clear"
                onClick={() => setQuery('')}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>

        <div
          className="max-h-[min(420px,50vh)] min-w-0 overflow-x-hidden overflow-y-auto px-3 py-2"
          data-testid="knowledge-scope-list"
        >
          {loading ? (
            <Text size="sm" color="muted" className="px-2 py-6 leading-5">
              {t('threads.scope.loading')}
            </Text>
          ) : tab === 'documents' ? (
            documents.length === 0 ? (
              <Text size="sm" color="muted" className="px-2 py-6 leading-5">
                {t('threads.scope.noDocuments')}
              </Text>
            ) : filteredDocuments.length === 0 ? (
              <Text size="sm" color="muted" className="px-2 py-6 leading-5">
                {t('threads.scope.noMatchingDocuments', { query: query.trim() })}
              </Text>
            ) : (
              filteredDocuments.map((doc) => {
                const checked = selectedDocs.has(doc.token)
                return (
                  <label
                    key={doc.token}
                    className={cn(
                      'flex min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-lg px-2 py-2 transition-colors',
                      checked ? 'bg-accent' : 'hover:bg-muted/50',
                    )}
                    data-testid={`knowledge-scope-doc-${doc.token}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleDoc(doc.token)}
                      data-testid={`knowledge-scope-doc-check-${doc.token}`}
                    />
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                      <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <span
                      className="min-w-0 max-w-full flex-1 truncate text-sm leading-5 text-foreground"
                      title={doc.originalFilename || t('threads.scope.untitledDocument')}
                    >
                      {doc.originalFilename || t('threads.scope.untitledDocument')}
                    </span>
                    <Badge variant="tag" className="shrink-0">
                      {fileTypeLabel(doc.originalFilename)}
                    </Badge>
                  </label>
                )
              })
            )
          ) : collections.length === 0 ? (
            <Text size="sm" color="muted" className="px-2 py-6 leading-5">
              {t('threads.scope.noCollections')}
            </Text>
          ) : filteredCollections.length === 0 ? (
            <Text size="sm" color="muted" className="px-2 py-6 leading-5">
              {t('threads.scope.noMatchingCollections', { query: query.trim() })}
            </Text>
          ) : (
            filteredCollections.map((collection) => {
              const checked = selectedCollections.has(collection.token)
              const count = collection.documentTokens?.length ?? 0
              return (
                <label
                  key={collection.token}
                  className={cn(
                    'flex min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-lg px-2 py-2 transition-colors',
                    checked ? 'bg-accent' : 'hover:bg-muted/50',
                  )}
                  data-testid={`knowledge-scope-collection-${collection.token}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleCollection(collection.token)}
                    data-testid={`knowledge-scope-collection-check-${collection.token}`}
                  />
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  <span
                    className="min-w-0 max-w-full flex-1 truncate text-sm leading-5 text-foreground"
                    title={collection.name}
                  >
                    {collection.name}
                  </span>
                  <Badge variant="tag" className="shrink-0">
                    {t('threads.scope.items', { count })}
                  </Badge>
                </label>
              )
            })
          )}
        </div>
      </div>
    </div>
  ) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] min-w-0 max-w-md flex-col gap-0 overflow-hidden p-0 sm:p-0 sm:rounded-xl"
        data-testid="knowledge-scope-picker"
      >
        <DialogHeader className="shrink-0 px-5 pb-3 pt-5 text-left">
          <DialogTitle className="font-sans text-base font-medium">{t('threads.scope.dialogTitle')}</DialogTitle>
          <DialogDescription className="mt-1 text-xs leading-5">
            {t('threads.scope.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex shrink-0 flex-col px-5 pb-3" data-testid="knowledge-scope-channels">
          <ChannelRow
            checked={isAuto}
            onCheckedChange={setAuto}
            icon={SlidersHorizontal}
            title={t('threads.scope.auto')}
            description={t('threads.scope.autoDescription')}
            testId="knowledge-scope-auto"
          />
          <ChannelRow
            checked={isAuto || library}
            onCheckedChange={switchSource(setLibrary)}
            icon={ENTITY_ICONS.library}
            title={t('threads.scope.chipLibrary')}
            description={t('threads.scope.libraryDescription')}
            testId="knowledge-scope-library"
          />
          <ChannelRow
            checked={isAuto || social}
            onCheckedChange={switchSource(setSocial)}
            icon={Radio}
            title={t('threads.scope.social')}
            description={t('threads.scope.socialDescription')}
            testId="knowledge-scope-social"
          />
          <ChannelRow
            checked={isAuto || web}
            onCheckedChange={switchSource(setWeb)}
            icon={Globe}
            title={t('threads.scope.chipWeb')}
            description={t('threads.scope.webDescription')}
            testId="knowledge-scope-web"
          />
        </div>

        {libraryFilter}

        <DialogFooter className="shrink-0 border-t border-border px-5 py-3 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="knowledge-scope-cancel"
            onClick={() => onOpenChange(false)}
          >
            {t('threads.scope.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="knowledge-scope-apply"
            disabled={!canApply}
            aria-disabled={!canApply}
            onClick={handleApply}
          >
            {selectedLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface RoomSourcesScopeProps extends PickerOpenProps, RoomSourcesProps {}

function RoomSourcesScope(props: RoomSourcesScopeProps) {
  const { open, onOpenChange, value, onApply, unavailable } = props
  const { t } = useTranslation('app')

  const [mode, setMode] = useState<RoomSourcesMode>(value.mode)
  const [library, setLibrary] = useState(value.library)
  const [social, setSocial] = useState(value.social)
  const [web, setWeb] = useState(value.web)

  useEffect(() => {
    if (!open) return
    setMode(value.mode)
    setLibrary(value.library)
    setSocial(value.social)
    setWeb(value.web)
  }, [open, value.mode, value.library, value.social, value.web])

  function handleApply() {
    // A source the room cannot use keeps its stored choice: the row only shows it off. Social
    // listening greyed out by the local Web search switch keeps the user's choice too.
    onApply({
      mode,
      library,
      social: unavailable.social === undefined ? social : value.social,
      web: unavailable.web === undefined ? web : value.web,
    })
    onOpenChange(false)
  }

  // Auto is the first switch. On, every source the room can use shows on; switching any one of
  // them by hand turns Auto off and keeps the others as shown. Turning Auto on again resets them.
  const isAuto = mode === 'auto'

  function setAuto(isOn: boolean) {
    setMode(isOn ? 'auto' : 'custom')
    if (isOn) {
      setLibrary(true)
      setSocial(true)
      setWeb(true)
    }
  }

  function switchSource(set: (isOn: boolean) => void) {
    return (isOn: boolean) => {
      if (isAuto) {
        setMode('custom')
        setLibrary(true)
        setSocial(true)
        setWeb(true)
      }
      set(isOn)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="min-w-0 max-w-md gap-0 overflow-hidden p-0 sm:p-0 sm:rounded-xl"
        data-testid="room-sources-picker"
      >
        <DialogHeader className="px-5 pb-3 pt-5 text-left">
          <DialogTitle className="font-sans text-base font-medium">{t('threads.scope.dialogTitle')}</DialogTitle>
          <DialogDescription className="mt-1 text-xs leading-5">
            {t('roomPane.sources.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col px-5 pb-3" data-testid="room-sources-rows">
          <ChannelRow
            checked={isAuto}
            onCheckedChange={setAuto}
            icon={SlidersHorizontal}
            title={t('roomPane.sources.modeAuto')}
            badge={t('roomPane.sources.recommended')}
            description={t('roomPane.sources.autoDescription')}
            testId="room-sources-toggle-auto"
            compact
          />
          <ChannelRow
            checked={isAuto || library}
            onCheckedChange={switchSource(setLibrary)}
            icon={ENTITY_ICONS.library}
            title={t('threads.scope.chipLibrary')}
            description={t('threads.scope.libraryDescription')}
            testId="room-sources-toggle-library"
            compact
          />
          <ChannelRow
            checked={isAuto || social}
            onCheckedChange={switchSource(setSocial)}
            icon={Radio}
            title={t('roomPane.sources.social')}
            description={t('roomPane.sources.socialDescription')}
            testId="room-sources-toggle-social"
            disabledReason={
              unavailable.social ??
              (isAuto || web ? undefined : t('roomPane.sources.needsWeb'))
            }
            compact
          />
          <ChannelRow
            checked={isAuto || web}
            onCheckedChange={switchSource(setWeb)}
            icon={Globe}
            title={t('roomPane.sources.web')}
            description={t('roomPane.sources.webDescription')}
            testId="room-sources-toggle-web"
            disabledReason={unavailable.web}
            compact
          />
        </div>

        <DialogFooter className="border-t border-border px-5 py-3 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="room-sources-cancel"
            onClick={() => onOpenChange(false)}
          >
            {t('threads.scope.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid="room-sources-apply"
            onClick={handleApply}
          >
            {t('threads.scope.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
